/**
 * Police-Ebene Schadenverlauf / Schadenauskunft — the SINGLE SOURCE OF TRUTH for the wire shapes,
 * vocabularies, labels, event names and pure helpers of the per-Police Schadenauskunft/Schadenrendement
 * (P2 Placement Slice 6; Detailkonzept Ausschreibung V3.0 Ch 6.2 / 7.2 / 7.3 / 11, P2 §7 Schritt 3, §8 E17).
 *
 * A `policy_claims_history` row carries TWO orthogonal axes (slice-6 design D1):
 *   - `status`   — the LIFECYCLE (aktuell | veraltet, Ch 6.2): exactly one `is_current` (= aktuell) row per policy.
 *   - `ergebnis` — the RESULT KIND (daten | leer): E17 (an empty answer is STORED as `leer`, never blocks).
 *
 * Mirrors the Slice-2 `POLICE_RISIKO_SOURCE` triad idiom (const + type + _VALUES + _LABELS). No number/date
 * formatters live here — the web uses `chf()`, `pct()`, `dateShortDe()` from `apps/web/src/lib/format.ts`.
 *
 * [Source: docs/product/p2-slices/slice-6-schadenverlauf-design.md §3]
 * [Source: packages/shared/src/police-risiko.ts (triad idiom)]
 * [Source: packages/shared/src/placement-snapshot.ts (Slice-5 wire-shape precedent)]
 */

// ── Vocabularies (Ch 6.2 / E17) ─────────────────────────────────────────────────

/** Lifecycle of a Schadenverlauf row (policy_claims_history.status CHECK; is_current ⇔ status='aktuell'). */
export const SCHADENVERLAUF_STATUS = {
  AKTUELL: 'aktuell',
  VERALTET: 'veraltet',
} as const;
export type SchadenverlaufStatus = (typeof SCHADENVERLAUF_STATUS)[keyof typeof SCHADENVERLAUF_STATUS];
export const SCHADENVERLAUF_STATUS_VALUES: readonly SchadenverlaufStatus[] = [
  SCHADENVERLAUF_STATUS.AKTUELL,
  SCHADENVERLAUF_STATUS.VERALTET,
];
export const SCHADENVERLAUF_STATUS_LABELS: Record<SchadenverlaufStatus, string> = {
  [SCHADENVERLAUF_STATUS.AKTUELL]: 'Aktuell',
  [SCHADENVERLAUF_STATUS.VERALTET]: 'Veraltet',
};

/** Result kind of a Schadenverlauf row (policy_claims_history.ergebnis CHECK; E17 `leer`). */
export const SCHADENVERLAUF_ERGEBNIS = {
  DATEN: 'daten',
  LEER: 'leer',
} as const;
export type SchadenverlaufErgebnis = (typeof SCHADENVERLAUF_ERGEBNIS)[keyof typeof SCHADENVERLAUF_ERGEBNIS];
export const SCHADENVERLAUF_ERGEBNIS_VALUES: readonly SchadenverlaufErgebnis[] = [
  SCHADENVERLAUF_ERGEBNIS.DATEN,
  SCHADENVERLAUF_ERGEBNIS.LEER,
];
export const SCHADENVERLAUF_ERGEBNIS_LABELS: Record<SchadenverlaufErgebnis, string> = {
  [SCHADENVERLAUF_ERGEBNIS.DATEN]: 'Daten vorhanden',
  [SCHADENVERLAUF_ERGEBNIS.LEER]: 'Keine Daten',
};

/** What triggered a Schadenverlauf retrieval (policy_claims_history.retrieval_trigger CHECK). */
export const SCHADENVERLAUF_TRIGGER = {
  PLACEMENT_CREATED: 'placement_created',
  MANUELL: 'manuell',
} as const;
export type SchadenverlaufTrigger = (typeof SCHADENVERLAUF_TRIGGER)[keyof typeof SCHADENVERLAUF_TRIGGER];
export const SCHADENVERLAUF_TRIGGER_VALUES: readonly SchadenverlaufTrigger[] = [
  SCHADENVERLAUF_TRIGGER.PLACEMENT_CREATED,
  SCHADENVERLAUF_TRIGGER.MANUELL,
];
export const SCHADENVERLAUF_TRIGGER_LABELS: Record<SchadenverlaufTrigger, string> = {
  [SCHADENVERLAUF_TRIGGER.PLACEMENT_CREATED]: 'Automatisch (Ausschreibung)',
  [SCHADENVERLAUF_TRIGGER.MANUELL]: 'Manuell',
};

// ── Constants ────────────────────────────────────────────────────────────────────

/** Rolling period length, in years, covered by one Schadenauskunft (Q45 — 5 years, stored per row). */
export const SCHADENVERLAUF_PERIODE_JAHRE = 5;

/** Max number of Verlauf rows returned on the list wire (newest first). */
export const SCHADENVERLAUF_VERLAUF_LIMIT = 20;

/** Human labels for the known provider/driver source ids (fallback = the raw source). */
export const SCHADENVERLAUF_SOURCE_LABELS: Record<string, string> = {
  fixture: 'Schadenauskunftsdienst (Demo-Fixture)',
};

/** Resolve a provider source id to its label; unknown sources fall back to the raw id. */
export function schadenverlaufSourceLabel(source: string): string {
  return SCHADENVERLAUF_SOURCE_LABELS[source] ?? source;
}

/** Sources whose data is synthetic demo data (the UI shows a «Demo-Daten» chip). */
export const SCHADENVERLAUF_DEMO_SOURCES: readonly string[] = ['fixture'];

/** E17 (P2 §8): an empty Schadenverlauf answer never blocks the tender — the warning text. */
export const SCHADENVERLAUF_LEER_HINWEIS =
  'Schadenverlauf nicht verfügbar - die Ausschreibung läuft ohne Schadenverlauf weiter.';

/** The empty state when no Schadenverlauf has been retrieved yet for a Police. */
export const SCHADENVERLAUF_NICHT_EINGEHOLT_HINWEIS = 'Schadenverlauf noch nicht eingeholt.';

/** Domain event names emitted by Slice 6 (same-tx audit + activity, no Kafka — master plan §7). */
export const SCHADENVERLAUF_EVENTS = {
  RETRIEVED: 'policy.claims_history_retrieved',
} as const;
export type SchadenverlaufEvent = (typeof SCHADENVERLAUF_EVENTS)[keyof typeof SCHADENVERLAUF_EVENTS];

// ── Wire types ─────────────────────────────────────────────────────────────────

/**
 * One Schadenverlauf entry on the list wire. `rohdaten_json` is NEVER on this shape — the dedicated
 * rohdaten endpoint serves it; the list only flags `has_rohdaten`.
 */
export interface SchadenverlaufEintrag {
  id: string;
  policy_id: string;
  placement_id: string | null;
  placement_number: string | null;
  retrieval_trigger: SchadenverlaufTrigger;
  source: string;
  source_label: string;
  provider_ref: string | null;
  retrieved_at: string;
  periode_von: string;
  periode_bis: string;
  ergebnis: SchadenverlaufErgebnis;
  schadenanzahl_5j: number | null;
  schadensumme_5j: number | null;
  schadenquote_rendement: number | null;
  has_rohdaten: boolean;
  rohdaten_ref: string | null;
  status: SchadenverlaufStatus;
  is_current: boolean;
  superseded_at: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  created_at: string;
}

/** Static descriptor of the active Schadenauskunftsdienst driver (UI provider info). */
export interface SchadenverlaufProviderInfo {
  driver: string;
  configured: boolean;
  is_demo: boolean;
}

/** GET /policies/:id/claims-history response (the Police «Schadenverlauf» panel). */
export interface PolicySchadenverlaufResponse {
  policy_id: string;
  current: SchadenverlaufEintrag | null;
  verlauf: SchadenverlaufEintrag[];
  provider: SchadenverlaufProviderInfo;
}

/** GET /policies/:id/claims-history/:entryId/rohdaten response (the Slice-8 Beilage payload). */
export interface SchadenverlaufRohdatenResponse {
  id: string;
  rohdaten_json: Record<string, unknown>;
  rohdaten_ref: string | null;
}

/**
 * The read-only Schadenverlauf reference embedded in the Ausschreibungs-Screen Tab 1 (Ch 7.3).
 *   - `available:false` + a reason (never a retrieval — Tab 1 is schreibgeschützt):
 *       'neugeschaeft'     — the placement has no Referenzpolice (E10).
 *       'nicht_eingeholt'  — a Referenzpolice exists but no Schadenverlauf has been retrieved yet.
 *       'permission'       — the caller lacks `policy_risk.read` or the Kachel is denied.
 *   - `available:true` — the current Schadenverlauf entry of the Referenzpolice.
 */
export type PlacementSchadenverlaufRef =
  | { available: false; reason: 'neugeschaeft' | 'nicht_eingeholt' | 'permission' }
  | { available: true; policy_id: string; eintrag: SchadenverlaufEintrag };

// ── Pure helpers ─────────────────────────────────────────────────────────────────

/** `d` → ISO date (YYYY-MM-DD), UTC. */
function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * The rolling Schadenverlauf period ending on `retrievedAt`: `bis` = that day, `von` = the same day
 * minus SCHADENVERLAUF_PERIODE_JAHRE years (UTC). A 29-Feb `bis` with no matching leap day
 * SCHADENVERLAUF_PERIODE_JAHRE years earlier normalises per JS Date arithmetic (→ 01 Mar).
 */
export function schadenverlaufPeriode(retrievedAt: Date): { von: string; bis: string } {
  const bis = new Date(
    Date.UTC(retrievedAt.getUTCFullYear(), retrievedAt.getUTCMonth(), retrievedAt.getUTCDate()),
  );
  const von = new Date(bis);
  von.setUTCFullYear(von.getUTCFullYear() - SCHADENVERLAUF_PERIODE_JAHRE);
  return { von: toIsoDate(von), bis: toIsoDate(bis) };
}

/** True when the entry is the E17 empty-answer kind. */
export function isSchadenverlaufLeer(e: Pick<SchadenverlaufEintrag, 'ergebnis'>): boolean {
  return e.ergebnis === SCHADENVERLAUF_ERGEBNIS.LEER;
}

/** True when the source is synthetic demo data (the «Demo-Daten» chip). */
export function isSchadenverlaufDemo(source: string): boolean {
  return SCHADENVERLAUF_DEMO_SOURCES.includes(source);
}
